<script>
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

export default {
  props: {
    services: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedHour: '01',
      selectedMinute: '00',
      servicesList: [...this.services],
      showObservations: false,
      currentName: '',
      currentObsKey: '',
      currentObsValue: '',
      obsToAddInService: {},

      errorMessagesList: [
        'Horário inválido, por favor, selecione outro horário para esse serviço',
        'O nome do serviço que você tentou adicionar já existe',
        'O horário de início deve ser maior que o horário de término',
        'Nome do serviço é inválido',
      ],
      errorMessage: '',
    };
  },
  computed: {
    // List of minutes 0..50, step of 10 minutes
    selectableMinutes() {
      return Array.from({ length: 6 }, (_, i) =>
        (i * 10).toString().padStart(2, '0')
      );
    },
    // List of hours 0..12
    selectableHours() {
      return [...Array(13)].map((_, i) => i.toString().padStart(2, '0'));
    },
  },
  methods: {
    showAlert(message) {
      emitter.emit('newToastMessage', message);
    },
    handleInput(inputContext) {
      const filtered = txt =>
        txt
          .trimStart()
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .slice(0, 40);
      const filteredKey = txt =>
        txt
          .trimStart()
          .replace(/[^a-z_]/g, '')
          .slice(0, 30);

      if (inputContext === 'obsValue')
        this.currentObsValue = filtered(this.currentObsValue);
      else if (inputContext === 'obsKey')
        this.currentObsKey = filteredKey(this.currentObsKey);
      else if (inputContext === 'name')
        this.currentName = filtered(this.currentName);
    },
    addObservation() {
      if (this.obsToAddInService[this.currentObsKey])
        useAlert('Já existe uma observação com essa chave');
      else if (!this.currentObsKey || !this.currentObsValue)
        useAlert('Observação inválida');
      else {
        this.$set(
          this.obsToAddInService,
          this.currentObsKey,
          this.currentObsValue
        );
        this.resetObsKeys();
      }
    },
    removeObservation(obsKey) {
      const { [obsKey]: _, ...filteredObj } = this.obsToAddInService;
      this.obsToAddInService = filteredObj;
    },
    obsToString(observations) {
      const observationsList = Object.entries(observations).map(
        ([chave, valor]) => ` ${chave}: ${valor}`
      );
      return observationsList.join();
    },
    removeSelectedService(selectedService) {
      this.servicesList = this.servicesList.filter(e => e !== selectedService);
      this.emitUpdatedServices();
    },
    activeObservation() {
      this.showObservations = !this.showObservations;
      this.resetObsKeys();
      this.obsToAddInService = {};
    },
    resetObsKeys() {
      this.currentObsKey = '';
      this.currentObsValue = '';
    },
    // eslint-disable-next-line consistent-return
    addService() {
      if (!this.validService()) return useAlert(this.errorMessage);

      const time = `${this.selectedHour}:${this.selectedMinute}`;
      const name = this.currentName;
      const key = name.replace(/\s+/g, '_').toLowerCase();
      const observations = this.obsToAddInService;
      const newService = { name, key, time, observations };

      this.servicesList.push(newService);
      this.emitUpdatedServices();

      this.currentName = '';
      this.obsToAddInService = {};
    },
    validService() {
      // Function to compare the selected time with list of selectable times
      const validTime = (val, validList) => validList.some(e => e === val);
      if (
        !this.selectedHour ||
        !this.selectedMinute ||
        !validTime(this.selectedHour, this.selectableHours) ||
        !validTime(this.selectedMinute, this.selectableMinutes) ||
        (this.selectedHour === '00' && this.selectedMinute === '00')
      ) {
        this.errorMessage = `❌  - ${this.errorMessagesList[0]}`;
        return false;
      }

      if (!this.currentName) {
        this.errorMessage = `❌  - ${this.errorMessagesList[3]}`;
        return false;
      }

      // Searching repeated service names in the list
      const filter = string => string.replace(/\s+/g, '').toUpperCase();
      if (
        this.servicesList.some(e => filter(e.name) === filter(this.currentName))
      ) {
        this.errorMessage = `❌  - ${this.errorMessagesList[1]}`;
        return false;
      }

      return true;
    },

    emitUpdatedServices() {
      this.$emit('updateServices', this.servicesList);
    },
  },
};
</script>

<template>
  <div>
    <div class="column">
      <div class="column">
        <label class="service-labels">Lista de serviços</label>
        <div v-if="!servicesList.length">
          <div class="row services-list">
            <input
              type="text"
              readonly
              value="Parece que ainda não existem serviços para essa agenda"
              class="text-center w-full text-red-500"
            />
          </div>
        </div>
        <div v-else>
          <div
            v-for="service in servicesList"
            :key="service.key"
            class="column mb-[10px]"
          >
            <div class="row services-list">
              <input
                type="text"
                readonly
                class="text-black"
                :value="`(${service.time}) ${service.name}`"
              />
              <button
                class="button custom-bg-color w-35"
                @click.prevent="removeSelectedService(service)"
              >
                x
              </button>
            </div>
            <p class="text-left">
              <strong>Obs:</strong>{{ obsToString(service.observations) }}
            </p>
          </div>
        </div>
        <div class="row add-service">
          <div class="column">
            <label class="service-labels h30">Nome</label>
            <input
              v-model="currentName"
              type="text"
              placeholder="Ex: Agendamento"
              class="text-sm w-full h35 rounded-none"
              @input="handleInput('name')"
            />
          </div>
          <div class="column">
            <label class="service-labels h30">Duração</label>
            <div class="row">
              <select v-model="selectedHour" class="select-time text-xs">
                <option disabled value="">Horas</option>
                <option v-for="hour in selectableHours" :key="hour">
                  {{ hour }}
                </option>
              </select>
              <select v-model="selectedMinute" class="select-time text-xs">
                <option disabled value="">Minutos</option>
                <option v-for="minute in selectableMinutes" :key="minute">
                  {{ minute }}
                </option>
              </select>
            </div>
          </div>
          <div class="column">
            <label class="service-labels row align h30 g-5">
              <span> Observação </span>
              <woot-switch
                size="small"
                :value="showObservations"
                @input="activeObservation"
              />
            </label>
            <div v-if="!showObservations" class="observation-row align first">
              <span class="text-xs"> Observações desligadas </span>
            </div>
            <div v-else>
              <!-- &.act -->
              <div class="observation-row align act first">
                <div class="row">
                  <div class="align ui">Chave</div>
                  <div class="align row">
                    <input
                      v-model="currentObsKey"
                      type="text"
                      placeholder="Ex: genero"
                      @input="handleInput('obsKey')"
                    />
                  </div>
                </div>
              </div>
              <div class="observation-row align act">
                <div class="row">
                  <div class="align ui">Valor</div>
                  <div class="align row">
                    <input
                      v-model="currentObsValue"
                      type="text"
                      placeholder="Ex: Masculino"
                      @input="handleInput('obsValue')"
                    />
                  </div>
                </div>
              </div>
              <button
                class="observation-row align act add-obs"
                @click.prevent="addObservation"
              >
                Adicionar observação
              </button>
              <div
                v-for="(observation, key) in obsToAddInService"
                :key="key"
                class="observation-row align act h40"
              >
                <div class="row">
                  <input
                    class="text-sm w-[calc(100%-40px)] border-0 h-[40px]"
                    type="text"
                    readonly
                    :value="`${key}: ${observation}`"
                  />
                  <woot-button
                    v-tooltip.top="'Excluir observação'"
                    variant="smooth"
                    color-scheme="alert"
                    icon="delete"
                    @click.prevent="removeObservation(key)"
                  />
                </div>
              </div>
            </div>
          </div>
          <button class="button green align" @click.prevent="addService">
            Adicionar<br />serviço
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.h30 {
  height: 30px;
}
.h35 {
  height: 35px;
}
.h40 {
  height: 40px;
}
.w-35 {
  width: 35px;
}
.g-5 {
  gap: 5px;
}
.custom-bg-color {
  background-color: var(--r-700);
}
.observation-row {
  border: 1px solid var(--s-200);
  height: 35px;
  text-align: center;
  &.add-obs {
    background-color: var(--v-900);
    font-size: var(--font-size-mini);
    color: white;
    width: 100%;
    &:hover {
      cursor: pointer;
    }
  }

  &.act {
    .row {
      width: 100%;
      height: 100%;
      .align.ui {
        width: 30%;
        border-right: 1px solid #ccd9e4;
        font-size: var(--font-size-mini);
      }
      .align.row {
        width: 70%;
        input {
          width: 100%;
          border-radius: 0;
          height: 30px;
          font-size: var(--font-size-mini);
          border: 0;
        }
      }
    }
  }
}
.align {
  display: flex;
  align-items: center;
  justify-content: center;
}
.button {
  box-shadow: 0px 1.5px 3px rgba(0, 0, 0, 0.486);
  transition: box-shadow 0.3s ease;
  background-color: var(--v-900);
  border: 0;
  border-radius: 0px;
  margin: 0;
  &.green {
    background-color: #1bbc9b;
    height: 65px;
  }
}
input[readonly] {
  margin: 0;
  border-radius: 0px;
  &:hover {
    cursor: pointer;
  }
}
.services-list {
  input {
    background-color: white;
    width: calc(100% - 35px);
  }
}
.service-labels {
  color: black;
  border-left: 1px solid #ccd9e4;
  border-right: 1px solid #ccd9e4;
  padding: 5px 0;
  background-color: var(--b-100);
}
.select-time {
  max-width: 50%;
  min-width: 72.5px;
  height: 35px;
  border-radius: 0px;
  margin: 0;
}
@media (max-width: 1020px) {
  .add-service {
    flex-flow: column;
  }
}
</style>
